import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import styles from "./AgentHistory.module.css";
import FilterHeader from "../FilterHeader";
import { CustomSwitch, InfiniteList2 } from "../../../../../../common/components";
import { AiAgentsService } from "../../../../../../api";
import { useApi } from "../../../../../../common/hooks";
import ExecutionRow from "./ExecutionRow";
import { HISTORY_TABS } from "../tabs";
import { isNonEmptyArray, openHtml } from "../../../../../../common/utils";
import { setCurrentDsiAgentSubpage } from "../../../../slice/dsi-slice";
import { translate } from "../../../../../../common/providers";
import { groupAgentExecutionRows } from "../../utils";

export default function AgentHistory() {
	const [sortDirection, setSortDirection] = useState("desc");
	const [sortKey, setSortKey] = useState("created");
	const [search, setSearch] = useState("");
	const [count, setCount] = useState(0);
	const [isGroupedView, setIsGroupedView] = useState(true);
	const [collapsedGroups, setCollapsedGroups] = useState({});
	const [infiniteListRequest, setInfiniteListRequest] = useState(null);
	const dispatch = useDispatch();
	const { call: getExecutionList } = useApi(AiAgentsService.getExecutionList);
	const { call: executeAgent } = useApi(AiAgentsService.executeAgent);
	const { call: deleteExecution } = useApi(AiAgentsService.deleteExecution);
	const { call: downloadTrace } = useApi(AiAgentsService.downloadTrace);
	useEffect(() => {
		setTimeout(() => {
			dispatch(setCurrentDsiAgentSubpage(1));
		}, 300);
	}, [dispatch]);
	useEffect(() => {
		setInfiniteListRequest(
			() =>
				({ page, limit }) =>
					getExecutionList({
						agentName: search,
						page,
						size: limit,
						sort: `${sortKey};${sortDirection}`,
					})
		);
	}, [search, count, sortKey, sortDirection, getExecutionList]);
	const handleChangeFilters = ({ name }) => {
		setSearch(name);
	};
	const handleClickSort = (newKey) => {
		if (newKey === sortKey) {
			setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
		} else {
			setSortKey(newKey);
			setSortDirection("asc");
		}
	};
	const handleRefresh = () => {
		setCount((prev) => prev + 1);
	};
	const handleLaunch = (payload) => {
		executeAgent(payload).then(handleRefresh).catch(console.error);
	};
	const handleDelete = (executionId) => {
		deleteExecution({ executionId }).then(handleRefresh).catch(console.error);
	};
	const handleDownloadTrace = (id) => {
		downloadTrace({ executionId: id })
			.then(({ data }) => {
				openHtml({ data });
			})
			.catch((err) => console.error(err));
	};
	const handleToggleGroup = (groupKey) => {
		setCollapsedGroups((prev) => ({
			...prev,
			[groupKey]: !prev[groupKey],
		}));
	};
	const groupedElementsMapper = useMemo(
		() =>
			(elements = []) => {
				const groupedElements = groupAgentExecutionRows(elements);
				return groupedElements.filter((execution) => {
					if (!execution?.parentExecutionId) {
						return true;
					}
					return collapsedGroups[execution.parentExecutionId] === false;
				});
			},
		[collapsedGroups]
	);
	const additionalFilter = useMemo(
		() => () =>
			(
				<div className={styles.groupToggleContainer}>
					<span>{translate("dsi.agents.execution-history.group-view")}</span>
					<CustomSwitch checked={isGroupedView} onChange={() => setIsGroupedView((prev) => !prev)} />
				</div>
			),
		[isGroupedView]
	);
	// The grouping metadata (__groupKey) lives on rows produced by groupAgentExecutionRows;
	// keep that convention here in the feature rather than leaking it into InfiniteList2.
	const getRowKey = (execution, index) =>
		// eslint-disable-next-line no-underscore-dangle
		execution?.id ?? `${execution?.__groupKey ?? "row"}-${index}`;
	const rowRenderer = (execution) => (
		<ExecutionRow
			key={execution?.id || "header"}
			execution={execution}
			isCollapsed={collapsedGroups[execution?.id] !== false}
			isHeader={isNonEmptyArray(execution)}
			sortDirection={sortDirection}
			sortKey={sortKey}
			onClickSort={handleClickSort}
			onDelete={handleDelete}
			onDownloadTrace={handleDownloadTrace}
			onLaunch={handleLaunch}
			onRefresh={handleRefresh}
			onRetry={handleLaunch}
			onToggleGroup={handleToggleGroup}
		/>
	);
	return (
		<div className={styles.container}>
			<FilterHeader
				actionTitle="dsi.agents.models.new.button"
				additionnalFilter={additionalFilter}
				onChangeFilters={handleChangeFilters}
			/>
			<div className={styles.list}>
				<InfiniteList2
					elementsMapper={isGroupedView ? groupedElementsMapper : null}
					getRowKey={getRowKey}
					headerRow={HISTORY_TABS}
					request={infiniteListRequest}
					rowRenderer={rowRenderer}
				/>
			</div>
		</div>
	);
}
